import { useEffect, useState } from "react";
import { produce } from "immer";

type ComparedType = Record<string, boolean>;

type DiffValue<T> = {
  from: T;
  to: T;
};

type DiffObject<T extends ComparedType> = {
  [K in keyof T]?: DiffValue<T[K]>;
};

export const useObjectDiff = <T extends ComparedType>(
  objA: T,
  objB: T
): DiffObject<T> => {
  const [diff, setDiff] = useState<DiffObject<T>>({});

  useEffect(() => {
    setDiff(diffObjects(objA, objB));
  }, [objA, objB]);

  return diff;
};

export const useObjectOnlyDiff = <T extends ComparedType>(
  objA: T,
  objB: T
): Partial<T> => {
  const [diff, setDiff] = useState<Partial<T>>({});

  useEffect(() => {
    setDiff(diffObjectsOnly(objA, objB));
  }, [objA, objB]);

  return diff;
};

/**
 * Return a top-level "from/to" for the differing keys between the object
 */
const diffObjects = <T extends ComparedType>(objA: T, objB: T) =>
  produce<DiffObject<T>, DiffObject<T>>({}, (draft) => {
    for (const key in objA) {
      if (objA[key] !== objB[key]) {
        draft[key] = { from: objA[key], to: objB[key] };
      }
    }
  });

/**
 * Return only the differing values between two objects
 */
const diffObjectsOnly = <T extends ComparedType>(objA: T, objB: T) =>
  produce<Partial<T>, T>({}, (draft) => {
    for (const key in objA) {
      if (objA[key] !== objB[key]) {
        draft[key] = objB[key];
      }
    }
  });
